import { SearchField } from "@/components/common/SearchField/SearchField"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useVirtualizer } from "@tanstack/react-virtual"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  useArticleList,
  useCountMatchingArticles,
  useProductGroups,
} from "../../../../api/articlesApi"
import type { ArticleRow } from "../../../../api/types/masterData"
import {
  decodeProductGroup,
  hasDistinctName,
  matchesArticleFilter,
  NO_PRODUCT_GROUP,
} from "./articleFilter"
import { ProductGroupSelect } from "./ProductGroupSelect"

export interface DraftArticle {
  id: string
  articleNumber: string
  name: string
  supplierArticleNumber?: string
  productGroup?: string
}

// Mirrors MAX_INQUIRY_ARTICLES in backend/turnus_ai/mc_constants.py: the backend
// rejects an article scope longer than this.
export const MAX_INQUIRY_ARTICLES = 20_000

// The list endpoint's largest page (_LIMIT_MAX in api/v2/mc_master_data.py).
const PAGE_LIMIT = 200
const SEARCH_DEBOUNCE_MS = 300

function toDraftArticle(r: ArticleRow, supplierId: string): DraftArticle {
  const supplierArticleNumber = r.suppliers.find(
    (s) => s.id === supplierId,
  )?.supplierArticleNumber
  return {
    id: r.id,
    articleNumber: r.articleNumber,
    name: r.name,
    ...(supplierArticleNumber ? { supplierArticleNumber } : {}),
    ...(r.productGroup ? { productGroup: r.productGroup } : {}),
  }
}

interface AddArticleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  supplierId: string
  scope: DraftArticle[] // the draft's current article list
  onConfirm: (articles: DraftArticle[]) => void
}

export function AddArticleDialog({
  open,
  onOpenChange,
  supplierId,
  scope,
  onConfirm,
}: AddArticleDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <AddArticleDialogBody
          supplierId={supplierId}
          scope={scope}
          onConfirm={onConfirm}
          onOpenChange={onOpenChange}
        />
      </DialogContent>
    </Dialog>
  )
}

function AddArticleDialogBody({
  supplierId,
  scope,
  onConfirm,
  onOpenChange,
}: {
  supplierId: string
  scope: DraftArticle[]
  onConfirm: (articles: DraftArticle[]) => void
  onOpenChange: (open: boolean) => void
}) {
  const { translate, currentLanguage } = useTranslation()
  const [search, setSearch] = useState("")
  const [picked, setPicked] = useState<Record<string, DraftArticle>>({})
  const [group, setGroup] = useState("")
  const [addingAll, setAddingAll] = useState(false)
  const [addAllError, setAddAllError] = useState(false)
  const [capHit, setCapHit] = useState(false)
  // Search runs on the server: the supplier can have more articles than the
  // pages loaded here, so a client-side filter would miss most of them.
  const query = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS)
  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isPlaceholderData,
  } = useArticleList({
    supplier_id: supplierId,
    // The membership an inquiry is seeded from (active links + legacy column),
    // not every junction row the master-data list would return.
    active_supplier_links: true,
    q: query || undefined,
    product_group: decodeProductGroup(group) ?? undefined,
    no_product_group: group === NO_PRODUCT_GROUP ? true : undefined,
    limit: PAGE_LIMIT,
  })
  const { data: orgGroups } = useProductGroups()

  const added = useMemo(() => new Set(scope.map((a) => a.id)), [scope])
  const remaining = Math.max(0, MAX_INQUIRY_ARTICLES - added.size)
  // What "add all" would add, from the candidate set the list renders: the
  // server's matches minus the scope. Exact once every page is loaded. While
  // more pages remain, the server total still counts scoped articles, so take
  // off the scope rows the server's own search matches (`useCountMatchingArticles`,
  // the same field set the list searches), or the loaded rows already added if
  // that is more. The walk in addAllMatching is exact either way.
  const total = data?.pages[0]?.total ?? 0
  const needle = query.toLowerCase()
  const scopeIds = useMemo(() => scope.map((a) => a.id), [scope])
  const { data: scopeMatchCount } = useCountMatchingArticles(
    {
      articleIds: scopeIds,
      q: query || undefined,
      supplierId,
      productGroup: decodeProductGroup(group) ?? undefined,
      noProductGroup: group === NO_PRODUCT_GROUP ? true : undefined,
    },
    !!hasNextPage,
  )
  const loadedRows: ArticleRow[] = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  )
  const filtered = useMemo(
    () => loadedRows.filter((r) => !added.has(r.id)),
    [loadedRows, added],
  )
  const toAdd = hasNextPage
    ? Math.max(
        0,
        total -
          Math.max(
            loadedRows.length - filtered.length,
            // Until the server answers: the old number, name and supplier
            // number match, a lower bound of the scope's matches.
            scopeMatchCount ??
              scope.filter((a) => matchesArticleFilter(a, needle, group))
                .length,
          ),
      )
    : filtered.length
  // Read by addAllMatching after each await: a changed search or a closed dialog
  // must abandon the walk, not add another query's rows.
  const live = useRef({ key: "", open: true })
  live.current.key = JSON.stringify([search.trim(), query, group])
  useEffect(() => {
    const state = live.current
    state.open = true
    return () => {
      state.open = false
    }
  }, [])
  // "Add all" walks the loaded query, so it must be the one the user typed:
  // not before the debounce fires, and not while the previous search's rows are
  // still on screen as placeholder data.
  const resultsCurrent = search.trim() === query && !isPlaceholderData
  const pickedCount = Object.keys(picked).length
  const overCap = pickedCount > remaining

  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 36,
    overscan: 8,
  })

  function toggle(r: ArticleRow) {
    setPicked((prev) => {
      const next = { ...prev }
      if (next[r.id]) delete next[r.id]
      else next[r.id] = toDraftArticle(r, supplierId)
      return next
    })
  }

  function confirm() {
    onConfirm(Object.values(picked))
    onOpenChange(false) // unmount on close resets picked/search
  }

  // Every match, not only the loaded pages: walk the cursor to the end. Stops
  // as soon as the matches would pass the cap and then adds nothing, so a
  // capped scope is never silently truncated.
  async function addAllMatching() {
    setAddAllError(false)
    setCapHit(false)
    setAddingAll(true)
    const startKey = live.current.key
    try {
      const all = new Map<string, DraftArticle>(Object.entries(picked))
      let result: Awaited<ReturnType<typeof fetchNextPage>> | undefined
      let pages = data?.pages ?? []
      let more = hasNextPage
      for (;;) {
        for (const page of pages) {
          for (const r of page.items) {
            if (!added.has(r.id)) all.set(r.id, toDraftArticle(r, supplierId))
          }
        }
        if (all.size > remaining) {
          setCapHit(true)
          return
        }
        if (!more) break
        result = await fetchNextPage()
        if (!live.current.open || live.current.key !== startKey) return
        if (result.isError) throw result.error
        pages = result.data?.pages.slice(-1) ?? []
        more = result.hasNextPage
      }
      onConfirm([...all.values()])
      onOpenChange(false)
    } catch {
      setAddAllError(true)
    } finally {
      if (live.current.open) setAddingAll(false)
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{translate("MC_INQ_DRAFT_ADD_ARTICLE_TITLE")}</DialogTitle>
        <DialogDescription className="sr-only">
          {translate("MC_INQ_DRAFT_ADD_ARTICLE_DESC")}
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Frozen while "add all" walks the pages: the walk belongs to exactly
            this search and group. */}
        <fieldset disabled={addingAll} className="contents">
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder={translate("MC_INQ_DRAFT_ADD_ARTICLE_SEARCH")}
            onClear={() => setSearch("")}
            className="max-w-none min-w-[16rem]"
          />
          <ProductGroupSelect
            options={orgGroups ?? []}
            value={group}
            onChange={setGroup}
            includeNone
          />
        </fieldset>
        {toAdd > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            disabled={addingAll || !resultsCurrent}
            onClick={() => void addAllMatching()}
          >
            {translate(
              addingAll
                ? "MC_INQ_DRAFT_ADD_ARTICLE_ALL_LOADING"
                : "MC_INQ_DRAFT_ADD_ARTICLE_ALL",
              { n: toAdd },
            )}
          </Button>
        )}
      </div>
      <div
        ref={scrollRef}
        className="max-h-72 overflow-y-auto rounded-lg border border-grey4"
      >
        {filtered.length === 0 && !hasNextPage ? (
          <div className="px-3 py-8 text-center text-sm text-grey5">
            {translate(
              isLoading
                ? "MC_INQ_DRAFT_ADD_ARTICLE_LOADING"
                : "MC_INQ_DRAFT_ADD_ARTICLE_EMPTY",
            )}
          </div>
        ) : (
          <>
            {/* Virtualized: a supplier can have five figures of articles, and
                every page the user loads used to add that many rows to the DOM. */}
            <div
              role="list"
              style={{
                height: virtualizer.getTotalSize(),
                position: "relative",
              }}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const r = filtered[virtualRow.index]
                return (
                  <label
                    key={r.id}
                    role="listitem"
                    // Windowed: the rows in the DOM are not the whole list.
                    aria-setsize={filtered.length}
                    aria-posinset={virtualRow.index + 1}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    className="flex cursor-pointer items-center gap-3 border-b border-grey4/50 px-3 py-2 hover:bg-white/4"
                  >
                    <Checkbox
                      checked={!!picked[r.id]}
                      onCheckedChange={() => toggle(r)}
                    />
                    <span className="font-mono text-[12px] text-yellow1">
                      {r.articleNumber}
                    </span>
                    {hasDistinctName(r) && (
                      <span className="truncate text-[12.5px] text-white">
                        {r.name}
                      </span>
                    )}
                  </label>
                )
              })}
            </div>
            {hasNextPage && (
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="w-full px-3 py-2.5 text-center text-[12px] text-grey5 hover:text-white disabled:opacity-50"
              >
                {translate(
                  isFetchingNextPage
                    ? "MC_INQ_DRAFT_ADD_ARTICLE_LOADING"
                    : "MC_INQ_DRAFT_ADD_ARTICLE_LOAD_MORE",
                )}
              </button>
            )}
          </>
        )}
      </div>
      {(overCap || capHit) && (
        <p className="text-[12px] text-red1">
          {translate("MC_INQ_DRAFT_ADD_ARTICLE_CAP", {
            max: MAX_INQUIRY_ARTICLES.toLocaleString(
              currentLanguage.toLowerCase(),
            ),
            n: remaining,
          })}
        </p>
      )}
      {addAllError && (
        <p className="text-[12px] text-red1">
          {translate("MC_INQ_DRAFT_ADD_ARTICLE_ALL_ERROR")}
        </p>
      )}
      <DialogFooter>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          {translate("MC_INQ_DRAFT_BTN_CANCEL")}
        </Button>
        <Button
          className="bg-yellow1 text-black hover:bg-yellow2"
          disabled={pickedCount === 0 || overCap || addingAll}
          onClick={confirm}
        >
          {translate("MC_INQ_DRAFT_ADD_ARTICLE_CONFIRM")}
        </Button>
      </DialogFooter>
    </>
  )
}
